<script setup lang="ts">
import { computed } from "vue"

const props = withDefaults(
  defineProps<{
    caption?: string
    label: string
    muted?: boolean
    progress?: number
    state?: "auto" | "empty" | "active" | "threshold"
    tone?: "primary" | "success" | "warning" | "error" | "neutral"
    trend?: string
    value: string
  }>(),
  {
    caption: "",
    muted: false,
    progress: undefined,
    state: "auto",
    tone: "neutral",
    trend: ""
  }
)

const normalizedValue = computed(() => props.value.trim().toLowerCase())
const numericValue = computed(() => {
  const cleaned = normalizedValue.value.replace(/[,%\s]/g, "")
  if (!/^-?\d+(\.\d+)?$/.test(cleaned)) {
    return null
  }
  return Number(cleaned)
})
const resolvedState = computed(() => {
  if (props.state !== "auto") {
    return props.state
  }
  if (
    props.muted ||
    normalizedValue.value === "" ||
    normalizedValue.value === "n/a" ||
    normalizedValue.value === "na" ||
    normalizedValue.value === "none" ||
    normalizedValue.value === "null" ||
    numericValue.value === 0
  ) {
    return "empty"
  }
  return "active"
})
const valueClass = computed(() => `dashboard-metric-value--${resolvedState.value}`)

const progressClass = computed(() => {
  if (props.tone === "primary") {
    return "dashboard-progress--primary"
  }
  if (props.tone === "success") {
    return "dashboard-progress--success"
  }
  if (props.tone === "warning") {
    return "dashboard-progress--warning"
  }
  if (props.tone === "error") {
    return "dashboard-progress--danger"
  }
  return ""
})
</script>

<template>
  <article class="dashboard-card dashboard-metric-card dashboard-reveal">
    <div class="flex h-full flex-col p-3.5">
      <div class="flex items-start justify-between gap-3">
        <div class="min-w-0">
          <p class="dashboard-label">{{ label }}</p>
          <p class="dashboard-mono dashboard-value mt-3 text-3xl font-medium leading-none" :class="valueClass">
            {{ value }}
          </p>
        </div>
        <span
          v-if="trend"
          class="dashboard-mono max-w-[9rem] border border-(--dash-line) px-2 py-1 text-right text-[0.68rem] leading-tight text-(--dash-muted)"
        >
          {{ trend }}
        </span>
      </div>

      <slot />

      <div v-if="progress !== undefined" class="dashboard-progress mt-4" :class="progressClass">
        <span :style="{ width: `${Math.max(0, Math.min(100, progress))}%` }" />
      </div>

      <p v-if="caption" class="mt-auto pt-3 text-sm leading-5 text-(--dash-muted)">
        {{ caption }}
      </p>
    </div>
  </article>
</template>
